import { Box, Typography } from '@mui/material';
import * as R from 'ramda';

import { marketing } from '@/assets';
import { EpisodeCoverImage, ModalContentContainer } from '@/components';
import { ComplianceTypes, ListenRejectedReasons, Modals } from '@/constants';
import { PlanActionButton } from '@/containers';
import { EVENT_CONSTANTS } from '@/services';
import { ComplianceType } from '@/types';

export const ListenRejectedModalReasons = R.omit(
  ['NEED_REDEEM', 'NOT_LOGGED_IN'],
  ListenRejectedReasons,
);
const EPISODE_IMG_SIZE = 160;
const LEFT_COLUMN_HEIGHT = EPISODE_IMG_SIZE + 60;

export type ListenRejectedModalReason =
  (typeof ListenRejectedModalReasons)[keyof typeof ListenRejectedModalReasons];

interface ListenRejectedModalProps {
  listenRejectedReason: ListenRejectedModalReason;
  compliance: ComplianceType;
  isB2CUser: boolean;
  close: () => void;
  fallbackButton?: React.ReactNode;
  episodeName?: string;
  episodeCover?: string;
}

export const ListenRejectedModalComponent = ({
  listenRejectedReason,
  compliance,
  close,
  fallbackButton = null,
  episodeName,
  episodeCover,
}: ListenRejectedModalProps) => {
  const title = getTitle(listenRejectedReason, compliance);
  const subtitle = getSubtitle(listenRejectedReason, compliance);
  const leftColumnContents = getLeftColumnContents(
    listenRejectedReason,
    episodeName,
    episodeCover,
  );

  return (
    <ModalContentContainer
      onModalClose={close}
      leftColumnContents={leftColumnContents}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: { xs: undefined, md: LEFT_COLUMN_HEIGHT },
          pr: 2,
        }}>
        <Typography variant="h3">{title}</Typography>
        <Typography variant="body2" sx={{ color: 'text.main', mb: 3 }}>
          {subtitle}
        </Typography>
        <Box sx={{ mt: 'auto' }}>
          {listenRejectedReason === ListenRejectedReasons.UNKNOWN ? (
            fallbackButton
          ) : (
            <PlanActionButton
              upgradeButtonLabel={getUpradeButtonLabel(listenRejectedReason)}
              fallbackComponent={fallbackButton}
              onClick={close}
              referrer={{
                component: EVENT_CONSTANTS.COMPONENT.MODAL,
                modalName: Modals.LISTEN_REJECTED,
                variants: {
                  modalVariant: listenRejectedReason,
                },
              }}
            />
          )}
        </Box>
      </Box>
    </ModalContentContainer>
  );
};

const getTitle = (
  listenRejectedReason: ListenRejectedModalReason,
  compliance: ComplianceType,
): string =>
  ({
    [ListenRejectedReasons.PLAN_USED_UP]: {
      [ComplianceTypes.CPE]: 'Need More CPE Credits?',
      [ComplianceTypes.CPD]: 'Need More CPD Hours?',
    },
    [ListenRejectedReasons.PLAN_EXPIRED]: {
      [ComplianceTypes.CPE]: 'Need More CPE Credits?',
      [ComplianceTypes.CPD]: 'Need More CPD Hours?',
    },
    [ListenRejectedReasons.ETHICS_LOCKED]: {
      [ComplianceTypes.CPE]: 'Ethics Content is for Subscribers Only',
      [ComplianceTypes.CPD]: 'Ethics Content is for Subscribers Only',
    },
    [ListenRejectedReasons.PREMIUM_LOCKED]: {
      [ComplianceTypes.CPE]: 'Premium Content is for Subscribers Only',
      [ComplianceTypes.CPD]: 'Premium Content is for Subscribers Only',
    },
    [ListenRejectedReasons.UNKNOWN]: {
      [ComplianceTypes.CPE]: 'Content Unavailable',
      [ComplianceTypes.CPD]: 'Content Unavailable',
    },
    [ListenRejectedReasons.FREE_TRIAL_ENDED]: {
      [ComplianceTypes.CPE]: "You're out of free credits, but not content.",

      [ComplianceTypes.CPD]: "You're out of free time, but not content.",
    },
  })[listenRejectedReason][compliance];

const getSubtitle = (
  listenRejectedReason: ListenRejectedModalReason,
  compliance: ComplianceType,
): string =>
  ({
    [ListenRejectedReasons.PLAN_USED_UP]: {
      [ComplianceTypes.CPE]: 'You have run out of credits.',
      [ComplianceTypes.CPD]: 'You have run out of hours.',
    },
    [ListenRejectedReasons.PLAN_EXPIRED]: {
      [ComplianceTypes.CPE]: 'Your plan has expired.',
      [ComplianceTypes.CPD]: 'Your plan has expired.',
    },
    [ListenRejectedReasons.ETHICS_LOCKED]: {
      [ComplianceTypes.CPE]:
        "This episode is part of LumiQ's ethics library. Upgrade now to unlock it and gain full access to 1,000+ verified CPE episodes.",
      [ComplianceTypes.CPD]:
        "This episode is part of LumiQ's ethics library. Upgrade now to unlock it and gain full access to 1,000+ verified CPD episodes.",
    },
    [ListenRejectedReasons.PREMIUM_LOCKED]: {
      [ComplianceTypes.CPE]:
        "This episode is part of LumiQ's premium or video library. Upgrade now to unlock it and gain full access to 1,000+ verified CPE episodes.",
      [ComplianceTypes.CPD]:
        "This episode is part of LumiQ's premium or video library. Upgrade now to unlock it and gain full access to 1,000+ verified CPD episodes.",
    },
    [ListenRejectedReasons.UNKNOWN]: {
      [ComplianceTypes.CPE]:
        'You are unable to play this episode at the moment.',
      [ComplianceTypes.CPD]:
        'You are unable to play this episode at the moment.',
    },
    [ListenRejectedReasons.FREE_TRIAL_ENDED]: {
      [ComplianceTypes.CPE]:
        "You've reached the end of your trial, but there are thousands of episodes still waiting. Upgrade now to keep your streak alive and unlock the full LumiQ library, including exclusive video and ethics content.",
      [ComplianceTypes.CPD]:
        "You've reached the end of your trial, but there are thousands of episodes still waiting. Upgrade now to keep your streak alive and unlock the full LumiQ library, including exclusive video and ethics content.",
    },
  })[listenRejectedReason][compliance];

const getUpradeButtonLabel = (
  listenRejectedReason: ListenRejectedModalReason,
): string => {
  switch (listenRejectedReason) {
    case ListenRejectedReasons.ETHICS_LOCKED:
    case ListenRejectedReasons.PREMIUM_LOCKED:
      return 'Unlock Access';
    case ListenRejectedReasons.FREE_TRIAL_ENDED:
      return 'Unlock Full Access';
    default:
      return 'Upgrade';
  }
};

const getLeftColumnContents = (
  listenRejectedReason: ListenRejectedModalReason,
  episodeName?: string,
  episodeCover?: string,
): React.ReactNode => {
  if (listenRejectedReason === ListenRejectedReasons.FREE_TRIAL_ENDED) {
    return (
      <Box
        component="img"
        src={marketing}
        alt="LumiQ marketing image"
        sx={{ width: LEFT_COLUMN_HEIGHT * 1.1, height: LEFT_COLUMN_HEIGHT }}
      />
    );
  }

  return !!episodeCover && !!episodeName ? (
    <Box sx={{ maxWidth: EPISODE_IMG_SIZE, mx: 'auto' }}>
      <EpisodeCoverImage src={episodeCover} dimension={EPISODE_IMG_SIZE} />
      <Typography
        variant="h4"
        sx={{
          color: 'text.contrast',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          WebkitLineClamp: 3,
        }}>
        {episodeName}
      </Typography>
    </Box>
  ) : undefined;
};
